import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Button } from '@goaltree/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@goaltree/ui/components/dialog'
import { Input } from '@goaltree/ui/components/input'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { ChevronRight, Repeat } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { coachErrorCode } from '@/components/coaching/coach-utils'
import { GivePlanDialog, type PlanSource } from '@/components/coaching/give-plan-dialog'
import { UserAvatar } from '@/components/user-avatar'

const LIBRARY_ERRORS = ['notFound', 'empty', 'tooMany', 'tooLong']

export function toastLibraryError(err: unknown, t: (k: string) => string) {
  const code = coachErrorCode(err)
  toast.error(t(`coachLibrary.errors.${code && LIBRARY_ERRORS.includes(code) ? code : 'generic'}`))
}

/** Saves a goal (the viewer's own, or a client's shared one) as a private template. */
export function useSavePrivateTemplate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const save = useMutation(api.coachTemplates.saveFromGoal)
  return async (goalId: Id<'nodes'>) => {
    try {
      await save({ goalId })
      toast.success(t('coachLibrary.saved'), {
        action: {
          label: t('coachLibrary.view'),
          onClick: () => void navigate({ to: '/coaching/library' }),
        },
      })
    } catch (err) {
      toastLibraryError(err, t)
    }
  }
}

export function PlanOutline({
  outline,
}: {
  outline: { title: string; milestones: { title: string; tasks: string[] }[]; habits: string[] }
}) {
  const { t } = useTranslation()
  return (
    <>
      <ol className="space-y-2.5">
        {outline.milestones.map((m, i) => (
          <li key={i}>
            <p className="text-sm font-semibold">{m.title}</p>
            <ul className="mt-0.5 space-y-0.5 pl-3 text-xs text-muted-foreground">
              {m.tasks.map((task, j) => (
                <li key={j}>· {task}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      {outline.habits.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-muted-foreground">{t('coachPlans.habits')}</p>
          <ul className="mt-0.5 space-y-0.5 text-xs">
            {outline.habits.map((h, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <Repeat className="size-3 text-muted-foreground" />
                {h}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

export function TemplateOutlineDialog({
  templateId,
  onClose,
  onGive,
}: {
  templateId: Id<'templates'> | null
  onClose: () => void
  onGive: (id: Id<'templates'>, title: string) => void
}) {
  const { t } = useTranslation()
  const outline = useQuery(api.coachTemplates.outline, templateId ? { id: templateId } : 'skip')
  return (
    <Dialog open={!!templateId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85dvh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>{outline?.title ?? t('coachLibrary.preview')}</DialogTitle>
          <DialogDescription>{t('coachLibrary.mineHint')}</DialogDescription>
        </DialogHeader>
        <div className="min-h-32 flex-1 overflow-y-auto rounded-2xl border bg-card px-4 py-3">
          {outline === undefined ? (
            <div className="h-32 animate-pulse rounded-xl bg-muted/40" />
          ) : outline === null ? (
            <p className="text-sm text-muted-foreground">{t('coachLibrary.errors.notFound')}</p>
          ) : (
            <PlanOutline outline={outline} />
          )}
        </div>
        <Button
          className="mt-4"
          disabled={!outline || !templateId}
          onClick={() => templateId && outline && onGive(templateId, outline.title)}
        >
          {t('coachLibrary.give')}
        </Button>
      </DialogContent>
    </Dialog>
  )
}

/** Own goals, then each client's shared goals, to snapshot as a template. */
export function GoalPickerDialog({
  open,
  title,
  onOpenChange,
  onPick,
}: {
  open: boolean
  title: string
  onOpenChange: (open: boolean) => void
  onPick: (goalId: Id<'nodes'>) => void | Promise<void>
}) {
  const { t } = useTranslation()
  const goals = useQuery(api.coachTemplates.pickableGoals, open ? {} : 'skip')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const q = search.trim().toLowerCase()
  const match = (g: { title: string }) => !q || g.title.toLowerCase().includes(q)
  const groups = goals
    ? [
        { key: 'own', label: t('coachLibrary.yourGoals'), image: undefined, goals: goals.own },
        ...goals.clients.map((c) => ({
          key: c.clientId,
          label: t('coachLibrary.clientGoals', { name: c.name }),
          image: { image: c.image, name: c.name },
          goals: c.goals,
        })),
      ]
        .map((g) => ({ ...g, goals: g.goals.filter(match) }))
        .filter((g) => g.goals.length)
    : undefined

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) setSearch('')
      }}
    >
      <DialogContent className="flex max-h-[85dvh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('coachLibrary.pickGoalDesc')}</DialogDescription>
        </DialogHeader>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('coachPlans.search')}
        />
        <div className="mt-3 min-h-40 flex-1 space-y-4 overflow-y-auto">
          {groups === undefined ? (
            <div className="h-40 animate-pulse rounded-2xl bg-muted/40" />
          ) : groups.length === 0 ? (
            <p className="px-1 py-4 text-sm text-muted-foreground">
              {q ? t('coachPlans.noResults') : t('coachLibrary.noGoals')}
            </p>
          ) : (
            groups.map((group) => (
              <section key={group.key}>
                <p className="mb-1.5 flex items-center gap-2 px-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {group.image && (
                    <UserAvatar
                      image={group.image.image}
                      name={group.image.name}
                      size={32}
                      className="size-4 rounded-full object-cover"
                    />
                  )}
                  <span className="truncate">{group.label}</span>
                </p>
                <div className="overflow-hidden rounded-2xl border">
                  {group.goals.map((g) => (
                    <button
                      key={g._id}
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true)
                        try {
                          await onPick(g._id)
                        } finally {
                          setBusy(false)
                        }
                      }}
                      className="flex w-full cursor-pointer items-center gap-3 border-t px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-muted/40 disabled:opacity-50"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{g.title}</p>
                        <p className="truncate text-xs text-muted-foreground capitalize">
                          {t(`discover.categories.${g.category.toLowerCase()}`, {
                            defaultValue: g.category,
                          })}
                        </p>
                      </div>
                      <ChevronRight className="size-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Pick a client, then the usual give step (new goal, or under one of their shared goals). */
export function GiveToClientDialog({
  plan,
  onClose,
}: {
  plan: { source: PlanSource; title: string } | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const clients = useQuery(api.coaching.clientPlans, plan ? {} : 'skip')
  const [clientId, setClientId] = useState<string | null>(null)
  const client = clients?.find((c) => c.clientId === clientId)
  const close = () => {
    setClientId(null)
    onClose()
  }

  if (plan && client)
    return (
      <GivePlanDialog
        key={`${client.clientId}:${JSON.stringify(plan.source)}`}
        clientId={client.clientId}
        clientName={client.client.name}
        goals={client.goals}
        open
        onOpenChange={(o) => !o && close()}
        initialSource={plan.source}
      />
    )

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && close()}>
      <DialogContent className="flex max-h-[85dvh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>
            {t('coachLibrary.pickClientTitle', { title: plan?.title ?? '' })}
          </DialogTitle>
          <DialogDescription>{t('coachLibrary.pickClientDesc')}</DialogDescription>
        </DialogHeader>
        <div className="min-h-32 flex-1 overflow-y-auto rounded-2xl border">
          {clients === undefined ? (
            <div className="h-32 animate-pulse bg-muted/40" />
          ) : clients.length === 0 ? (
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground">{t('coachLibrary.noClients')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => {
                  close()
                  void navigate({ to: '/coaching' })
                }}
              >
                {t('coachingMode.inviteClient')}
              </Button>
            </div>
          ) : (
            clients.map((c) => (
              <button
                key={c.clientId}
                type="button"
                onClick={() => setClientId(c.clientId)}
                className="flex w-full cursor-pointer items-center gap-3 border-t px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-muted/40"
              >
                <UserAvatar
                  image={c.client.image}
                  name={c.client.name}
                  size={64}
                  className="size-8 rounded-full object-cover"
                />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                  {c.client.name}
                </span>
                <ChevronRight className="size-4 text-muted-foreground" />
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
